import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import React, { useState, useEffect } from "react";
import { can } from "../utils/moduleAccess";


function pathOpensMenu(pathname) {
  const pathLower = pathname.toLowerCase();
  const match = (routes) =>
    routes.some((route) => pathLower.startsWith(route.toLowerCase()));

  return {
    procurement:
      match(["/supplyMaster"]) ||
      match([
        "/procurementTracker",
        "/procurementTrackerAdd",
        "/procurementTrackerEdit",
        "/procurementView",
        "/procurementTrackerImport",
      ]) ||
      match(["/rfq", "/rfqCreate", "/rfqView", "/rfqComparison"]) ||
      match([
        "/priceTracker",
        "/priceTrackerAdd",
        "/priceTrackerEdit",
        "/priceTrackerView",
      ]) ||
      match(["/po", "/poCreate", "/poEdit", "/poView"]),
    supplierRfq: match(["/rfqSupplierList", "/rfqSupplierView"]),
    supplierPo: match(["/poSupplierList", "/poSupplierView"]),
    supplierGi: match(["/giSupplierList", "/giSupplierView"]),
    customerSalesQuote: match(["/customerSalesQuoteList", "/customerSalesQuoteView"]),
    customerSalesOrder: match(["/customerSalesOrderList", "/customerSalesOrderView"]),
    sales:
      match([
        "/customerMaster",
        "/customerMasterAdd",
        "/customerMasterEdit",
        "/customerMasterView",
      ]) ||
      match([
        "/salesTracker",
        "/salesTrackerAdd",
        "/salesTrackerEdit",
        "/salesTrackerView",
      ]) ||
      match([
        "/salesQuotes",
        "/salesQuotesAdd",
        "/salesQuotesEdit",
        "/salesQuotesView",
      ]) ||
      match([
        "/salesOrder",
        "/salesOrderCreate",
        "/salesOrderEdit",
        "/salesOrderView",
      ]),
    inventory:
      match([
        "/inventory",
        "/inventoryAdd",
        "/inventoryEdit",
        "/inventoryView"
      ]),
    goodsInwards:
      match([
        "/goodsInwards",
        "/goodsInwardsCreate",
        "/goodsInwardsView"
      ]),
    goodsOutwards:
      match([
        "/goodsOutwards",
        "/goodsOutwardsCreate",
        "/goodsOutwardsView",
      ]),

    operations:
      match(["/soConverter", "/soValidate", "/sodata"]) ||
      match(["/trackerUpdate", "/trackerUpdateAdd", "/trackerView"]) ||
      match([
        "/paymentTracker",
        "/paymentTrackerAdd",
        "/paymentTrackerEdit",
        "/paymentView",
      ]) ||
      match([
        "/frightTracker",
        "/frightTrackerCreate",
        "/frightTrackerView",
        "/frightTrackerEdit",
      ]) ||
      match(["/task", "/taskAdd", "/taskEdit", "/taskView"]),
    masterData:
      match(["/category", "/categoryAdd", "/categoryEdit", "/categoryView"]) ||
      match(["/subcategory"]) ||
      match(["/material", "/materialCreate", "/materialView", "/materialEdit"]) ||
      match(["/unit"]),
    settings:
      match(["/user", "/UserAdd", "/userView", "/userEdit"]) ||
      match(["/roles", "/roles/add", "/roles/edit"]),

      logistics: match([
        "/logisticsRfq",
        "/logisticsRfqCreate",
        "/logisticsRfqEdit",
        "/logisticsRfqView",
        "/logisticsRfqComparison",
      ]),
    };
  }

const Sidebar = () => {
  const { pathname } = useLocation();
  const user = useSelector((state) => state.user);

  const userRole = user?.role || localStorage.getItem("role");
  const userPermissions =
    user?.permissions ||
    JSON.parse(localStorage.getItem("permissions") || "[]");

  // Menu visibility: flat permissions + moduleAccess.view (when Role Access is set).
  // Form lookups (get-suppliers / get-customers / get-users) do NOT unlock these menus.
  const SUPERADMIN_ALLOWED_KEYS = new Set(["dashboard", "user_management", "business_management"]);

 const hasPermission = (key) => {
  const role = String(userRole || "").trim().toLowerCase();


  if (role === "superadmin") {
    return SUPERADMIN_ALLOWED_KEYS.has(key);
  }

  if (role === "admin") {
    return key !== "business_management";
  }

  if (!userPermissions.includes(key)) {
    return false;
  }

  return can(user, key, "view");
};

  const hasSupplierPermission = () =>
    hasPermission("supplier_master_view") ||
    hasPermission("supplier_master_all") ||
    (userPermissions.includes("supply_master") &&
      can(user, "supply_master", "view"));

  const hasCustomerPermission = () =>
    hasPermission("customer_master_view") ||
    hasPermission("customer_master_all") ||
    (userPermissions.includes("customer_master") &&
      can(user, "customer_master", "view"));

  const matchRoutes = (routes) => {
    const pathLower = pathname.toLowerCase();
    return routes.some((route) =>
      pathLower.startsWith(route.toLowerCase())
    );
  };

  const isSupplyMasterActive = () =>
    matchRoutes(["/supplyMaster"]);

  const isProcurementTrackerActive = () =>
    matchRoutes([
      "/procurementTracker",
      "/procurementTrackerAdd",
      "/procurementTrackerEdit",
      "/procurementView",
      "/procurementTrackerImport",
    ]);

  const isPriceTrackerActive = () =>
    matchRoutes([
      "/priceTracker",
      "/priceTrackerAdd",
      "/priceTrackerEdit",
      "/priceTrackerView",
    ]);

  const isRfqActive = () =>
    matchRoutes(["/rfq", "/rfqCreate", "/rfqView", "/rfqComparison"]);

  const isSupplierRfqActive = () =>
    matchRoutes(["/rfqSupplierList", "/rfqSupplierView"]);

  const isSupplierPoActive = () =>
    matchRoutes(["/poSupplierList", "/poSupplierView"]);

  const isSupplierGiActive = () =>
    matchRoutes(["/giSupplierList", "/giSupplierView"]);

  const isPoActive = () =>
    matchRoutes(["/po", "/poCreate", "/poEdit", "/poView"]);

  const isSqActive = () =>
    matchRoutes(["/customerSalesQuoteList", "/customerSalesQuoteView",]);

  const isSoActive = () =>
    matchRoutes(["/customerSalesOrderList", "/customerSalesOrderView",]);

  const isGoodsInwardsActive = () =>
    matchRoutes([
      "/goodsInwards",
      "/goodsInwardsCreate",
      "/goodsInwardsView"
    ]);

  const isGoodsOutwardsActive = () =>
    matchRoutes([
      "/goodsOutwards",
      "/goodsOutwardsCreate",
      "/goodsOutwardsView",
    ]);

  const isCustomerActive = () =>
    matchRoutes([
      "/customerMaster",
      "/customerMasterAdd",
      "/customerMasterEdit",
      "/customerMasterView",
    ]);

  const isSalesTrackerActive = () =>
    matchRoutes([
      "/salesTracker",
      "/salesTrackerAdd",
      "/salesTrackerEdit",
      "/salesTrackerView",
    ]);

  const isSalesQuotesActive = () =>
    matchRoutes([
      "/salesQuotes",
      "/salesQuotesAdd",
      "/salesQuotesEdit",
      "/salesQuotesView",
    ]);

  const isSalesOrderActive = () =>
    matchRoutes([
      "/salesOrder",
      "/salesOrderCreate",
      "/salesOrderEdit",
      "/salesOrderView",
    ]);

  const isEcatalogueActive = () => matchRoutes(["/ecatalogue"]);

  const isInventoryActive = () =>
    matchRoutes([
      "/inventory",
      "/inventoryAdd",
      "/inventoryEdit",
      "/inventoryView",
    ]);

  const isCategoryActive = () =>
    matchRoutes([
      "/category",
      "/categoryAdd",
      "/categoryEdit",
      "/categoryView"
    ]);

  const isSubcategoryActive = () =>
    matchRoutes(["/subcategory"]);

  const isMaterialActive = () =>
    matchRoutes([
      "/material",
      "/materialCreate",
      "/materialView",
      "/materialEdit",
    ]);

  const isUnitActive = () => matchRoutes(["/unit"]);

  const isAttributeActive = () => matchRoutes(["/attribute"]);

  const isSoConverterActive = () =>
    matchRoutes(["/soConverter", "/soValidate", "/sodata"]);

  const isTrackerUpdateActive = () =>
    matchRoutes(["/trackerUpdate", "/trackerUpdateAdd", "/trackerView"]);

  const isPaymentTrackerActive = () =>
    matchRoutes([
      "/paymentTracker",
      "/paymentTrackerAdd",
      "/paymentTrackerEdit",
      "/paymentView",
    ]);

  const isFrightTrackerActive = () =>
    matchRoutes([
      "/frightTracker",
      "/frightTrackerCreate",
      "/frightTrackerView",
      "/frightTrackerEdit",
    ]);

  const isTaskTrackerActive = () =>
    matchRoutes(["/task", "/taskAdd", "/taskEdit", "/taskView"]);

  const isUsersActive = () =>
    matchRoutes([
      "/user",
      "/UserAdd",
      "/userView",
      "/userEdit",
    ]);

  const isRoleActive = () =>
    matchRoutes([
      "/roles",
      "/roles/add",
      "/roles/edit",
    ]);

    const isBusinessActive = () =>
    matchRoutes([
      "/business",
    ]);

  const isLogisticsRfqActive = () =>
    matchRoutes([
      "/logisticsRfq",
      "/logisticsRfqCreate",
      "/logisticsRfqEdit",
      "/logisticsRfqView",
      "/logisticsRfqComparison",
    ]);


  const [openMenus, setOpenMenus] = useState({
    procurement: false,
    logistics: false, 
    sales: false,
    operations: false,
    masterData: false,
    settings: false,
  });

  /** Open sections that match the current route; never auto-close user-expanded menus. */
  useEffect(() => {
    const auto = pathOpensMenu(pathname);
    setOpenMenus((prev) => ({
      ...prev,
      ...(auto.procurement && { procurement: true }),
      ...(auto.logistics && { logistics: true }),
      ...(auto.sales && { sales: true }),
      ...(auto.operations && { operations: true }),
      ...(auto.masterData && { masterData: true }),
      ...(auto.settings && { settings: true }),
    }));
  }, [pathname]);

  const toggleMenu = (key) => {
    setOpenMenus((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const getNavLink = (condition) =>
    condition ? "nav-link active" : "nav-link";

  return (
    <aside className="main-sidebar sidebar-dark-primary elevation-4">
      <Link to="/dashboard" className="brand-link logo-switch">
        <img
          src="/assets/img/color icon_circle.png"
          alt="logo"
          className="brand-image-xl logo-xs"
        />
        <img
          src="/assets/img/color icon_circle.png"
          alt="GeoHunt360"
          className="brand-image-xs logo-xl"
        />
      </Link>

      <div className="sidebar">
        <nav className="mt-2">
          <ul className="nav nav-pills nav-sidebar flex-column">

            {/* DASHBOARD */}
            {hasPermission("dashboard") && (
              <li className="nav-item">
                <Link to="/dashboard" className={getNavLink(pathname === "/dashboard")}>
                  <i className="nav-icon fa-solid fa-gauge-simple-high" />
                  <p>Dashboard</p>
                </Link>
              </li>
            )}

            {hasPermission("ai_chat") && (
              <li className="nav-item">
                <Link to="/aiChat" className={getNavLink(pathname === "/aiChat")}>
                  <i className="nav-icon fa-solid fa-robot" />
                  <p>AI Sourcing Agent</p>
                </Link>
              </li>
            )}

            {/* PROCUREMENT */}
            {(hasSupplierPermission() ||
              hasPermission("procurement_tracker") ||
              hasPermission("rfq") ||
              hasPermission("price_tracker") ||
              hasPermission("po")) && (
                <li className={`nav-item has-treeview ${openMenus.procurement ? "menu-open" : ""}`}>
                  <a
                    href="#sidebar-procurement"
                    className={getNavLink(openMenus.procurement)}
                    onClick={(e) => {
                      e.preventDefault();
                      toggleMenu("procurement");
                    }}
                  >
                    <i className="nav-icon fa-solid fa-boxes" />
                    <p>
                      Procurement
                      <i className="right fas fa-angle-right"
                        style={{
                          transform: openMenus.procurement ? "rotate(90deg)" : "rotate(0deg)",
                          transition: "0.3s",
                        }}
                      />
                    </p>
                  </a>

                  <ul className="nav nav-treeview" style={{ display: openMenus.procurement ? "block" : "none" }}>
                    {hasSupplierPermission() && (
                      <li className="nav-item">
                        <Link to="/supplyMaster" className={getNavLink(isSupplyMasterActive())}>
                          <i className="nav-icon fa-solid fa-store" />
                          <p>Supplier Master</p>
                        </Link>
                      </li>
                    )}

                    {hasPermission("procurement_tracker") && (
                      <li className="nav-item">
                        <Link to="/procurementTracker" className={getNavLink(isProcurementTrackerActive())}>
                          <i className="nav-icon fa-solid fa-clipboard-list" />
                          <p>Purchase Request</p>
                        </Link>
                      </li>
                    )}

                    {hasPermission("rfq") && (
                      <li className="nav-item">
                        <Link to="/rfq" className={getNavLink(isRfqActive())}>
                          <i className="nav-icon fa-solid fa-envelope-open-text" />
                          <p>RFQ</p>
                        </Link>
                      </li>
                    )}

                    {hasPermission("po") && (
                      <li className="nav-item">
                        <Link to="/po" className={getNavLink(isPoActive())}>
                          <i className="nav-icon fa-solid fa-file-contract" />
                          <p>Purchase Order</p>
                        </Link>
                      </li>
                    )}

                    {hasPermission("price_tracker") && (
                      <li className="nav-item">
                        <Link to="/priceTracker" className={getNavLink(isPriceTrackerActive())}>
                          <i className="nav-icon fa-solid fa-tags" />
                          <p>Price List</p>
                        </Link>
                      </li>
                    )}


                  </ul>
                </li>
              )}

            {String(userRole || "")
              .trim()
              .toLowerCase() !== "admin" &&
              hasPermission("supplier_rfq") && (
              <li className="nav-item">
                <Link
                  to="/rfqSupplierList"
                  className={getNavLink(isSupplierRfqActive())}
                >
                  <i className="nav-icon fa-solid fa-envelope-open-text" />
                  <p> RFQ</p>
                </Link>
              </li>
            )}

              {String(userRole || "")
              .trim()
              .toLowerCase() !== "admin" &&
              hasPermission("supplier_po") && (
              <li className="nav-item">
                <Link
                  to="/poSupplierList"
                  className={getNavLink(isSupplierPoActive())}
                >
                  <i className="nav-icon fa-solid fa-file-contract" />
                  <p>Purchase Order</p>
                </Link>
              </li>
            )}

              {String(userRole || "")
              .trim()
              .toLowerCase() !== "admin" &&
              hasPermission("supplier_gi") && (
              <li className="nav-item">
                <Link
                  to="/giSupplierList"
                  className={getNavLink(isSupplierGiActive())}
                >
                   <i className="nav-icon fa-solid fa-boxes-stacked" />
                  <p>Goods Inward</p>
                </Link>
              </li>
            )}

              {String(userRole || "")
              .trim()
              .toLowerCase() !== "admin" &&
              hasPermission("customer_sq") && (
              <li className="nav-item">
                <Link
                  to="/customerSalesQuoteList"
                  className={getNavLink(isSqActive())}
                >
                  <i className="nav-icon fa-solid fa-envelope-open-text" />
                  <p> Sales Quote</p>
                </Link>
              </li>
            )}

             {String(userRole || "")
              .trim()
              .toLowerCase() !== "admin" &&
              hasPermission("customer_so") && (
              <li className="nav-item">
                <Link
                  to="/customerSalesOrderList"
                  className={getNavLink(isSoActive())}
                >
                  <i className="nav-icon fa-solid fa-envelope-open-text" />
                  <p> Sales Order</p>
                </Link>
              </li>
            )}

           {hasPermission("logistics") && (
              <li
                className={`nav-item has-treeview ${openMenus.logistics ? "menu-open" : ""}`}
              >
                <a
                  href="#sidebar-logistics"
                  className={getNavLink(openMenus.logistics)}
                  onClick={(e) => {
                    e.preventDefault();
                    toggleMenu("logistics");
                  }}
                >
                  <i className="nav-icon fa-solid fa-truck" />
                  <p>
                    Logistics
                    <i
                      className="right fas fa-angle-right"
                      style={{
                        transform: openMenus.logistics
                          ? "rotate(90deg)"
                          : "rotate(0deg)",
                        transition: "0.3s",
                      }}
                    />
                  </p>
                </a>

                <ul
                  className="nav nav-treeview"
                  style={{ display: openMenus.logistics ? "block" : "none" }}
                >
                  {hasPermission("logistics") && (
                    <li className="nav-item">
                      <Link
                        to="/logisticsRfq"
                        className={getNavLink(isLogisticsRfqActive())}
                      >
                        <i className="nav-icon fa-solid fa-envelope-open-text" />
                        <p>Logistics RFQ</p>
                      </Link>
                    </li>
                  )}
                </ul>
              </li>
            )}

            {/* INVENTORY */}
            {(hasPermission("inventory_management")) && (
              <li className={`nav-item has-treeview ${openMenus.inventory ? "menu-open" : ""}`}>
                <a href="#sidebar-inventory" className={getNavLink(openMenus.inventory)} onClick={(e) => {
                  e.preventDefault();
                  toggleMenu("inventory");
                }}>
                  <i className="nav-icon fa-solid fa-boxes-stacked" />
                  <p>
                    Inventory
                    <i className="right fas fa-angle-right"
                      style={{
                        transform: openMenus.inventory ? "rotate(90deg)" : "rotate(0deg)",
                        transition: "0.3s",
                      }}
                    />
                  </p>
                </a>
                <ul className="nav nav-treeview" style={{ display: openMenus.inventory ? "block" : "none" }}>
                  {hasPermission("inventory_management") && (
                    <li className="nav-item">
                      <Link to="/inventory" className={getNavLink(isInventoryActive())}>
                        <i className="nav-icon fa-solid fa-boxes-stacked" />
                        <p>Inventory List</p>
                      </Link>
                    </li>
                  )}
                </ul>
                {hasPermission("goods_inwards") && (
                  <ul className="nav nav-treeview" style={{ display: openMenus.inventory ? "block" : "none" }}>
                    <li className="nav-item">
                      <Link to="/goodsInwards" className={getNavLink(isGoodsInwardsActive())}>
                        <i className="nav-icon fa-solid fa-boxes-stacked" />
                        <p>Goods Inwards</p>
                      </Link>
                    </li>
                  </ul>
                )}
                {hasPermission("goods_outwards") && (
                  <ul className="nav nav-treeview" style={{ display: openMenus.inventory ? "block" : "none" }}>
                    <li className="nav-item">
                      <Link to="/goodsOutwards" className={getNavLink(isGoodsOutwardsActive())}>
                        <i className="nav-icon fa-solid fa-boxes-stacked" />
                        <p>Goods Outwards</p>
                      </Link>
                    </li>
                  </ul>
                )}
              </li>
            )}

            {/* SALES */}
            {(hasCustomerPermission ()||
              hasPermission("sales_tracker") ||
              hasPermission("sales_quotes") ||
              hasPermission("sales_order")) && (
                <li className={`nav-item has-treeview ${openMenus.sales ? "menu-open" : ""}`}>
                  <a
                    href="#sidebar-sales"
                    className={getNavLink(openMenus.sales)}
                    onClick={(e) => {
                      e.preventDefault();
                      toggleMenu("sales");
                    }}
                  >
                    <i className="nav-icon fa-solid fa-chart-line" />
                    <p>
                      Sales
                      <i className="right fas fa-angle-right"
                        style={{
                          transform: openMenus.sales ? "rotate(90deg)" : "rotate(0deg)",
                          transition: "0.3s",
                        }}
                      />
                    </p>
                  </a>

                  <ul className="nav nav-treeview" style={{ display: openMenus.sales ? "block" : "none" }}>
                    {hasCustomerPermission() && (
                      <li className="nav-item">
                        <Link to="/customerMaster" className={getNavLink(isCustomerActive())}>
                          <i className="nav-icon fa-solid fa-address-book" />
                          <p>Customer Master</p>
                        </Link>
                      </li>
                    )}

                    {hasPermission("sales_tracker") && (
                      <li className="nav-item">
                        <Link to="/salesTracker" className={getNavLink(isSalesTrackerActive())}>
                          <i className="nav-icon fa-solid fa-chart-line" />
                          <p>Sales Tracker</p>
                        </Link>
                      </li>
                    )}

                    {hasPermission("sales_quotes") && (
                      <li className="nav-item">
                        <Link to="/salesQuotes" className={getNavLink(isSalesQuotesActive())}>
                          <i className="nav-icon fa-solid fa-file-invoice" />
                          <p>Sales Quotes</p>
                        </Link>
                      </li>
                    )}

                    {hasPermission("sales_order") && (
                      <li className="nav-item">
                        <Link to="/salesOrder" className={getNavLink(isSalesOrderActive())}>
                          <i className="nav-icon fa-solid fa-file-invoice-dollar" />
                          <p>Sales Orders</p>
                        </Link>
                      </li>
                    )}
                  </ul>
                </li>
              )}

            {/* E-CATALOGUE */}
            {hasPermission("ecatalogue") && (
              <li className="nav-item">
                <Link to="/ecatalogue" className={getNavLink(isEcatalogueActive())}>
                  <i className="nav-icon fa-solid fa-book-open" />
                  <p>E-Catalogue</p>
                </Link>
              </li>
            )}

            {/* OPERATIONS */}
            {(hasPermission("so_converter") ||
              hasPermission("tracker_update") ||
              hasPermission("payment_tracker") ||
              hasPermission("freight_tracker") ||
              hasPermission("task_management")) && (
                <li className={`nav-item has-treeview ${openMenus.operations ? "menu-open" : ""}`}>
                  <a
                    href="#sidebar-operations"
                    className={getNavLink(openMenus.operations)}
                    onClick={(e) => {
                      e.preventDefault();
                      toggleMenu("operations");
                    }}
                  >
                    <i className="nav-icon fa-solid fa-gears" />
                    <p>
                      Operations
                      <i className="right fas fa-angle-right"
                        style={{
                          transform: openMenus.operations ? "rotate(90deg)" : "rotate(0deg)",
                          transition: "0.3s",
                        }}
                      />
                    </p>
                  </a>

                  <ul className="nav nav-treeview" style={{ display: openMenus.operations ? "block" : "none" }}>
                    {hasPermission("so_converter") && (
                      <li className="nav-item">
                        <Link to="/soConverter" className={getNavLink(isSoConverterActive())}>
                          <i className="nav-icon fa-solid fa-file-export" />
                          <p>SO Converter</p>
                        </Link>
                      </li>
                    )}

                    {hasPermission("tracker_update") && (
                      <li className="nav-item">
                        <Link to="/trackerUpdate" className={getNavLink(isTrackerUpdateActive())}>
                          <i className="nav-icon fa-solid fa-file-lines" />
                          <p>Customer Order Report</p>
                        </Link>
                      </li>
                    )}

                    {hasPermission("payment_tracker") && (
                      <li className="nav-item">
                        <Link to="/paymentTracker" className={getNavLink(isPaymentTrackerActive())}>
                          <i className="nav-icon fa-solid fa-money-bill-wave" />
                          <p>Payment Tracker</p>
                        </Link>
                      </li>
                    )}

                    {hasPermission("freight_tracker") && (
                      <li className="nav-item">
                        <Link to="/frightTracker" className={getNavLink(isFrightTrackerActive())}>
                          <i className="nav-icon fa-solid fa-truck-fast" />
                          <p>Freight Tracker</p>
                        </Link>
                      </li>
                    )}

                    {hasPermission("task_management") && (
                      <li className="nav-item">
                        <Link to="/task" className={getNavLink(isTaskTrackerActive())}>
                          <i className="nav-icon fa-solid fa-list-check" />
                          <p>Task</p>
                        </Link>
                      </li>
                    )}
                  </ul>
                </li>
              )}



            {/* Master Data */}
            {(hasPermission("category_management") ||
              hasPermission("subcategory_management") ||
              hasPermission("material_management") ||
              hasPermission("unit_management")||hasPermission("attribute_management")) && (
                <li className={`nav-item has-treeview ${openMenus.masterData ? "menu-open" : ""}`}>
                  <a
                    href="#sidebar-master-data"
                    className={getNavLink(openMenus.masterData)}
                    onClick={(e) => {
                      e.preventDefault();
                      toggleMenu("masterData");
                    }}
                  >
                    <i className="nav-icon fa-solid fa-database" />
                    <p>
                      Master Data
                      <i className="right fas fa-angle-right"
                        style={{
                          transform: openMenus.masterData ? "rotate(90deg)" : "rotate(0deg)",
                          transition: "0.3s",
                        }}
                      />
                    </p>
                  </a>
                  <ul className="nav nav-treeview" style={{ display: openMenus.masterData ? "block" : "none" }}>
                    {hasPermission("category_management") && (
                      <li className="nav-item">
                        <Link to="/category" className={getNavLink(isCategoryActive())}>
                          <i className="nav-icon fa-solid fa-layer-group" />
                          <p>Category</p>
                        </Link>
                      </li>
                    )}

                    {hasPermission("subcategory_management") && (
                      <li className="nav-item">
                        <Link to="/subcategory" className={getNavLink(isSubcategoryActive())}>
                          <i className="nav-icon fa-solid fa-sitemap" />
                          <p>Subcategory</p>
                        </Link>
                      </li>
                    )}

                    {hasPermission("material_management") && (
                      <li className="nav-item">
                        <Link to="/material" className={getNavLink(isMaterialActive())}>
                          <i className="nav-icon fa-solid fa-cubes" />
                          <p>Material</p>
                        </Link>
                      </li>
                    )}

                    {hasPermission("unit_management") && (
                      <li className="nav-item">
                        <Link to="/unit" className={getNavLink(isUnitActive())}>
                          <i className="nav-icon fa-solid fa-ruler" />
                          <p>Unit</p>
                        </Link>
                      </li>
                    )}
                     {hasPermission("attribute_management") && (
                      <li className="nav-item">
                        <Link to="/attribute" className={getNavLink(isAttributeActive())}>
                          <i className="nav-icon fa-solid fa-tags" />
                          <p>Brand/OEM</p>
                        </Link>
                      </li>
                    )}
                  </ul>
                </li>
              )}

            {/* SETTINGS */}
            {(hasPermission("user_management") ||
              hasPermission("role_management")||  hasPermission("business_management")) && (
                <li className={`nav-item has-treeview ${openMenus.settings ? "menu-open" : ""}`}>
                  <a
                    href="#sidebar-settings"
                    className={getNavLink(openMenus.settings)}
                    onClick={(e) => {
                      e.preventDefault();
                      toggleMenu("settings");
                    }}
                  >
                    <i className="nav-icon fa-solid fa-building" />
                    <p>
                      Settings
                      <i className="right fas fa-angle-right"
                        style={{
                          transform: openMenus.settings ? "rotate(90deg)" : "rotate(0deg)",
                          transition: "0.3s",
                        }}
                      />
                    </p>
                  </a>

                  <ul className="nav nav-treeview" style={{ display: openMenus.settings ? "block" : "none" }}>
                    {hasPermission("user_management") && (
                      <li className="nav-item">
                        <Link to="/user" className={getNavLink(isUsersActive())}>
                          <i className="nav-icon fa-solid fa-user" />
                          <p>Users</p>
                        </Link>
                      </li>
                    )}

                    {hasPermission("role_management") && (
                      <li className="nav-item">
                        <Link to="/roles" className={getNavLink(isRoleActive())}>
                          <i className="nav-icon fa-solid fa-user-shield" />
                          <p>Roles</p>
                        </Link>
                      </li>
                    )}

                     {hasPermission("business_management") && (
                      <li className="nav-item">
                        <Link to="/business" className={getNavLink(isBusinessActive())}>
                          <i className="nav-icon fa-solid fa-briefcase" />
                          <p>Business</p>
                        </Link>
                      </li>
                    )}
                  </ul>
                </li>
              )}

          </ul>
        </nav>
      </div>
    </aside>
  );
};

export default Sidebar;